import { useEffect, useRef, useState } from 'react';

export interface DelayedFlagOptions {
  /** Anti-flash: `active` must stay raised this long before the flag shows. */
  readonly showAfterMs?: number;
  /** Anti-blink: once shown, the flag stays visible at least this long. */
  readonly minVisibleMs?: number;
}

/**
 * Debounced visibility flag for transient activity indicators
 * (2026-06-10 control-plane flicker fix, RC1).
 *
 * Problem: a background poll settles in ~50-300ms, so a naive
 * `{refreshing ? <chip/> : null}` mounts/unmounts the indicator for a few
 * milliseconds every cycle — a visible blink that reads as "the page reloads
 * every 10s".
 *
 * Contract:
 *  - `active` bursts shorter than `showAfterMs` NEVER show (fast polls render
 *    nothing);
 *  - once shown, the flag stays true for at least `minVisibleMs` (no blink);
 *  - if `active` rises again while a hide is pending, the hide is cancelled;
 *  - all timers are cleaned up on dep change and unmount.
 *
 * no-silent-stale: this debounces only the INDICATOR. The underlying data
 * still revalidates every cycle, and a slow (>showAfterMs) refresh is still
 * surfaced to the operator.
 */
export function useDelayedFlag(
  active: boolean,
  { showAfterMs = 300, minVisibleMs = 600 }: DelayedFlagOptions = {},
): boolean {
  const [visible, setVisible] = useState(false);
  // Timestamp of the moment the flag actually became visible — drives the
  // minVisibleMs hold on the falling edge.
  const shownAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearTimer = (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (active && !visible) {
      // Rising edge: arm the show-delay. If `active` drops before it fires
      // (the common fast poll), the cleanup below cancels it — no flash.
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        shownAtRef.current = Date.now();
        setVisible(true);
      }, showAfterMs);
    } else if (!active && visible) {
      // Falling edge: honor the minimum visible window before hiding.
      clearTimer();
      const remaining = minVisibleMs - (Date.now() - shownAtRef.current);
      if (remaining <= 0) {
        setVisible(false);
      } else {
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          setVisible(false);
        }, remaining);
      }
    } else {
      // active && visible  → stay visible (a pending hide was cancelled by
      //                      the effect cleanup when `active` rose again);
      // !active && !visible → stay hidden (a pending show was cancelled).
      clearTimer();
    }

    return clearTimer;
  }, [active, visible, showAfterMs, minVisibleMs]);

  return visible;
}
